import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  collection, query, where, orderBy, limit, startAfter,
  getDocs, QueryDocumentSnapshot, DocumentData, onSnapshot
} from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { Match, PublicMatch, Player, UserProfile } from '../../types';
import { Search, ChevronDown, Loader2 } from 'lucide-react';
import { PairAvatar } from './PairAvatar';
import { LazyItem } from './LazyItem';
import { formatPairName } from '../../lib/utils';
import { HistoryMatchDetailModal } from './HistoryMatchDetailModal';

const PAGE_SIZE = 15;

export interface UnifiedMatch {
  id: string;
  source: 'tournament' | 'matchmaking';
  dateStr: string;
  timestamp: number;
  tournamentName: string;
  matchTypeStr: string;
  team1: {
    name: string;
    p1Id: string;
    p2Id: string;
  };
  team2: {
    name: string;
    p1Id: string;
    p2Id: string;
  };
  scoreStr: string;
  team1Won: boolean;
  team2Won: boolean;
  participantIds: string[];
  locationName: string;
  phaseStr?: string;
  originalMatch: Match | PublicMatch;
}

interface UnifiedHistoryProps {
  currentUserProfile: UserProfile;
  activeTab: 'mis_partidos_oficiales' | 'todos_partidos_oficiales';
  players: Player[];
  onPlayerClick: (playerId: string) => void;
  onMatchClick?: (match: UnifiedMatch) => void;
  globalCity?: string;
  sedesPublicas?: UserProfile[];
}

// --- Helper: convert a raw Firestore Match doc into a UnifiedMatch ---
function tournamentMatchToUnified(
  m: Match,
  teamsMap: Map<string, { name: string; player1Id?: string; player2Id?: string }>,
  sedesPublicas?: UserProfile[]
): UnifiedMatch | null {
  if (m.status !== 'completed') return null;
  const t1 = teamsMap.get(m.team1Id);
  const t2 = teamsMap.get(m.team2Id);
  if (!t1 || !t2) return null;

  const pIds = (m.participantIds || []).filter(Boolean);
  
  const timestampToUse = (m as any).completedAt || (m as any).reportedAt || m.createdAt || 0;
  const d = new Date(timestampToUse);

  let locationName = 'Sede Oficial';
  if (sedesPublicas && m.organizerId) {
    const org = sedesPublicas.find(s => s.uid === m.organizerId);
    if (org && org.city) {
      locationName = org.city;
    }
  }

  let phaseStr = 'Torneo';
  if (m.zoneName) {
    const zName = m.zoneName.toLowerCase().startsWith('zona') ? m.zoneName : `Zona ${m.zoneName}`;
    phaseStr = `Fase de Grupos - ${zName}`;
  } else if (m.round || m.tournamentRound) {
    const r = m.round || m.tournamentRound;
    phaseStr = `Eliminatoria - ${r}`;
  }

  return {
    id: `t_${m.id}`,
    source: 'tournament',
    dateStr: timestampToUse
      ? d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
      : '-',
    timestamp: timestampToUse,
    tournamentName: m.tournamentName || 'Liga Oficial',
    matchTypeStr: 'Torneo',
    phaseStr,
    team1: {
      name: formatPairName(t1.name, 'short'),
      p1Id: t1.player1Id || '',
      p2Id: t1.player2Id || ''
    },
    team2: {
      name: formatPairName(t2.name, 'short'),
      p1Id: t2.player1Id || '',
      p2Id: t2.player2Id || ''
    },
    scoreStr: m.score || [m.s1, m.s2, m.s3].filter(Boolean).join(' '),
    team1Won: m.winnerId === m.team1Id,
    team2Won: m.winnerId === m.team2Id,
    participantIds: pIds,
    locationName,
    originalMatch: m
  };
}

function publicMatchToUnified(m: PublicMatch, players: Player[], sedesPublicas?: UserProfile[]): UnifiedMatch | null {
  const reportedResult = (m as any).reportedResult;
  if (!reportedResult || (m.status !== 'completed' && (m as any).status !== 'finished')) return null;

  let p1Id = '', p2Id = '', p3Id = '', p4Id = '';
  if (m.slotMapping) {
    p1Id = m.slotMapping[0] || '';
    p2Id = m.slotMapping[1] || '';
    p3Id = m.slotMapping[2] || '';
    p4Id = m.slotMapping[3] || '';
  } else {
    p1Id = m.players[0] as string || '';
    p2Id = m.players[1] as string || '';
    p3Id = m.players[2] as string || '';
    p4Id = m.players[3] as string || '';
  }

  let t1Sets = 0, t2Sets = 0;
  [reportedResult.s1, reportedResult.s2, reportedResult.s3].filter(Boolean).forEach(s => {
    const [s1, s2] = s!.split('-').map(Number);
    if (s1 > s2) t1Sets++;
    else if (s2 > s1) t2Sets++;
  });

  const getSur = (pid: string) => {
    if (!pid) return '';
    const p = players.find(x => x.id === pid);
    if (!p) return '';
    const parts = p.name.trim().split(' ');
    return parts.length === 1 ? parts[0] : `${parts[0].charAt(0)}. ${parts[parts.length - 1]}`;
  };

  const timestampToUse = (m as any).completedAt || (m as any).reportedAt || new Date(`${m.date}T${m.time}`).getTime();
  const d = new Date(timestampToUse);

  let locationName = 'Sede Oficial';
  if (m.clubId && sedesPublicas) {
    const club = sedesPublicas.find(s => s.uid === m.clubId);
    if (club && club.name) {
      locationName = club.name;
    } else {
      const clubPlayer = players.find(p => p.id === m.clubId);
      if (clubPlayer && clubPlayer.name) locationName = clubPlayer.name;
    }
  } else {
    const creator = players.find(p => p.id === m.creatorId);
    if (creator && creator.city) {
      locationName = creator.city;
    }
  }

  return {
    id: `m_${m.id}`,
    source: 'matchmaking',
    dateStr: d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }),
    timestamp: timestampToUse,
    tournamentName: m.mode === 'oficial' ? 'Partido Oficial' : 'Partido Amistoso',
    matchTypeStr: m.matchType === 'super8' ? 'Super 8' : 'Desafío 2v2',
    team1: { name: [getSur(p1Id), getSur(p2Id)].filter(Boolean).join(' / '), p1Id, p2Id },
    team2: { name: [getSur(p3Id), getSur(p4Id)].filter(Boolean).join(' / '), p1Id: p3Id, p2Id: p4Id },
    scoreStr: [reportedResult.s1, reportedResult.s2, reportedResult.s3].filter(Boolean).join(' '),
    team1Won: t1Sets > t2Sets,
    team2Won: t2Sets > t1Sets,
    participantIds: [p1Id, p2Id, p3Id, p4Id].filter(Boolean),
    locationName,
    originalMatch: m
  };
}

export function UnifiedHistory({
  currentUserProfile,
  activeTab,
  players,
  onPlayerClick,
  onMatchClick,
  globalCity,
  sedesPublicas
}: UnifiedHistoryProps) {
  const [searchHistory, setSearchHistory] = useState('');
  const [sortHistoryDesc, setSortHistoryDesc] = useState(true);

  // --- Tournament matches (paginated) ---
  const [tournamentMatches, setTournamentMatches] = useState<Match[]>([]);
  const [lastTournamentDoc, setLastTournamentDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMoreTournament, setHasMoreTournament] = useState(true);
  const [loadingTournament, setLoadingTournament] = useState(false);

  // --- Teams cache (fetched on demand) ---
  const [teamsMap, setTeamsMap] = useState<Map<string, { name: string; player1Id?: string; player2Id?: string }>>(new Map());

  const [publicMatches, setPublicMatches] = useState<PublicMatch[]>([]);

  const [selectedHistoryMatch, setSelectedHistoryMatch] = useState<UnifiedMatch | null>(null);

  const loadingRef = useRef(false);

  // Build the Firestore query for tournament matches
  // We avoid orderBy or multiple wheres to prevent composite index requirements
  const buildTournamentQuery = useCallback(() => {
    const col = collection(db, getCollectionPath('matches'));
    if (activeTab === 'mis_partidos_oficiales') {
      return query(
        col,
        where('participantIds', 'array-contains', currentUserProfile.uid)
      );
    } else {
      // For 'todos_partidos_oficiales' we just fetch the most recent ones
      // We will sort and filter in JS
      return query(col, orderBy('createdAt', 'desc'), limit(100));
    }
  }, [activeTab, currentUserProfile.uid]);

  // Initial load + reset when tab changes
  useEffect(() => {
    setTournamentMatches([]);
    setLastTournamentDoc(null);
    setHasMoreTournament(false); // We fetch all or top 100 directly without pagination for now
    setLoadingTournament(true);
    loadingRef.current = false;

    const q = buildTournamentQuery();
    getDocs(q).then(async (snap) => {
      let docs = snap.docs.map(d => ({ id: d.id, ...(d.data() as Record<string, unknown>) } as unknown as Match));
      // Local filter for status if needed (buildTournamentQuery already doesn't filter status for mis_partidos)
      docs = docs.filter(m => m.status === 'completed');
      docs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setTournamentMatches(docs);
      setLastTournamentDoc(null);
      setHasMoreTournament(false);

      // Fetch teams referenced by these matches
      const teamIds = new Set<string>();
      docs.forEach(m => { teamIds.add(m.team1Id); teamIds.add(m.team2Id); });
      await fetchTeams([...teamIds]);
      setLoadingTournament(false);
    });
  }, [activeTab, currentUserProfile.uid, buildTournamentQuery]);

  // Load more (pagination) disabled as we fetch locally
  const loadMore = useCallback(async () => {
    // No-op since we do local sorting/fetching
  }, []);

  // Fetch teams and merge into cache
  const fetchTeams = useCallback(async (ids: string[]) => {
    const missing = ids.filter(id => id && !teamsMap.has(id));
    if (missing.length === 0) return;

    // Firestore 'in' query supports max 30 items per batch
    const batches: string[][] = [];
    for (let i = 0; i < missing.length; i += 30) batches.push(missing.slice(i, i + 30));

    const newEntries = new Map<string, { name: string; player1Id?: string; player2Id?: string }>();
    for (const batch of batches) {
        const { documentId } = await import('firebase/firestore');
        const snap = await getDocs(query(
        collection(db, getCollectionPath('teams')),
        where(documentId(), 'in', batch)
      ));
      snap.docs.forEach(d => {
        const data = d.data();
        newEntries.set(d.id, { name: data.name || '', player1Id: data.player1Id, player2Id: data.player2Id });
      });
    }
    setTeamsMap(prev => new Map([...prev, ...newEntries]));
  }, [teamsMap]);

  // Real-time listener for public (matchmaking) official matches — limited
  useEffect(() => {
    let q;
    if (activeTab === 'mis_partidos_oficiales') {
      // Use 'players' field — all public matches have this field with UIDs of joined players
      q = query(
        collection(db, getCollectionPath('public_matches')),
        where('players', 'array-contains', currentUserProfile.uid)
      );
    } else {
      q = query(
        collection(db, getCollectionPath('public_matches')),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE * 2)
      );
    }

    const unsub = onSnapshot(q, (snap) => {
      let pms = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as PublicMatch))
        .filter(m => (m.status === 'completed' || m.status === 'finished') && m.mode === 'oficial');
      
      // Sort in JS by date
      pms.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      
      // Slice to prevent huge arrays
      pms = pms.slice(0, PAGE_SIZE * 2);
      
      setPublicMatches(pms);
    });
    return () => unsub();
  }, [activeTab, currentUserProfile.uid]);

  // Convert raw matches → unified format
  const unifiedMatches = useMemo<UnifiedMatch[]>(() => {
    const list: UnifiedMatch[] = [];
    tournamentMatches.forEach(m => {
      if (globalCity && globalCity !== 'Todas las ciudades' && activeTab === 'todos_partidos_oficiales') {
        const org = (sedesPublicas || []).find(s => s.uid === m.organizerId);
        if (org && org.city && org.city !== globalCity) return;
      }
      const u = tournamentMatchToUnified(m, teamsMap, sedesPublicas);
      if (u) list.push(u);
    });
    publicMatches.forEach(m => {
      if (globalCity && globalCity !== 'Todas las ciudades' && activeTab === 'todos_partidos_oficiales') {
        const org = (sedesPublicas || []).find(s => s.uid === m.clubId);
        if (org && org.city) {
          if (org.city !== globalCity) return;
        } else {
          const creator = players.find(p => p.id === m.creatorId);
          if (creator && creator.city && creator.city !== globalCity) return;
        }
      }
      const u = publicMatchToUnified(m, players, sedesPublicas);
      if (u) list.push(u);
    });
    return list.sort((a, b) => sortHistoryDesc ? b.timestamp - a.timestamp : a.timestamp - b.timestamp);
  }, [tournamentMatches, publicMatches, teamsMap, players, sortHistoryDesc, globalCity, activeTab, sedesPublicas]);

  const filteredMatches = useMemo(() => {
    if (!searchHistory) return unifiedMatches;
    const q = searchHistory.toLowerCase();
    return unifiedMatches.filter(m => {
      const playerMatch = m.participantIds.some(pid => {
        const p = players.find(x => x.id === pid);
        return p && p.name.toLowerCase().includes(q);
      });
      return (
        m.team1.name.toLowerCase().includes(q) ||
        m.team2.name.toLowerCase().includes(q) ||
        m.tournamentName.toLowerCase().includes(q) ||
        m.matchTypeStr.toLowerCase().includes(q) ||
        playerMatch
      );
    });
  }, [unifiedMatches, searchHistory, players]);

  return (
    <>
      <div className="w-full flex justify-end mb-6">
        <div className="w-full md:w-auto flex flex-col sm:flex-row items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 opacity-30" size={18} />
            <input
              type="text"
              placeholder="Buscar equipo, jugador o torneo..."
              value={searchHistory}
              onChange={(e) => setSearchHistory(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-2xl py-3 pl-11 pr-4 focus:theme-primary-border outline-none transition-all placeholder:opacity-30 text-white"
            />
          </div>
          <button
            onClick={() => setSortHistoryDesc(!sortHistoryDesc)}
            className="w-full sm:w-auto bg-white/5 border border-white/10 rounded-2xl px-6 py-3 font-bold flex items-center justify-center gap-2 hover:bg-white/10 transition-colors text-white"
          >
            {sortHistoryDesc ? 'Más Recientes' : 'Más Antiguos'}
          </button>
        </div>
      </div>

      <div className="theme-bg-card backdrop-blur-3xl rounded-2xl md:rounded-[var(--radius-large)] border border-white/10 overflow-hidden shadow-2xl">
        {/* DESKTOP TABLE */}
        <div className="hidden md:block overflow-x-auto no-scrollbar">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-white/[0.07] text-[10px] font-black uppercase tracking-[0.2em] border-b border-white/10">
                <th className="px-8 py-5 border-r border-white/5 text-white/60">Fecha</th>
                <th className="px-8 py-5 text-center border-r border-white/5 text-white/60">Encuentro</th>
                <th className="px-8 py-5 text-center border-r border-white/5 text-white/60">Evento</th>
                <th className="px-8 py-5 text-center text-white/60">Resultado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredMatches.map((match, index) => (
                <LazyItem
                  key={match.id}
                  as="tr"
                  estimatedHeight={78}
                  onClick={() => setSelectedHistoryMatch(match)}
                  className={`${index % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent'} hover:bg-white/[0.05] transition-colors group cursor-pointer`}
                >
                  <td className="px-8 py-5 border-r border-white/5">
                    <span className="text-[11px] font-black font-mono opacity-30 leading-none">FECHA</span>
                    <div className="text-sm font-bold mt-1 text-white">{match.dateStr}</div>
                  </td>
                  <td className="px-8 py-5 border-r border-white/5">
                    <div className="flex items-center justify-center gap-12">
                      <div className="flex items-center gap-3 min-w-[150px] justify-end">
                        <span className={`text-sm font-black tracking-tighter transition-colors uppercase italic ${match.team1Won ? 'theme-primary-text' : 'opacity-30 group-hover:opacity-60'}`}>
                          {match.team1.name}
                        </span>
                        <PairAvatar
                          p1Id={match.team1.p1Id} p2Id={match.team1.p2Id}
                          p1Image={players.find(p => p.id === match.team1.p1Id)?.photoUrl}
                          p2Image={players.find(p => p.id === match.team1.p2Id)?.photoUrl}
                          className="w-8 h-8" onPlayerClick={onPlayerClick}
                        />
                      </div>
                      <div className="relative">
                        <span className="text-xs font-black theme-primary-text italic tracking-[0.2em] drop-shadow-[0_0_10px_rgba(var(--glow-rgb), 0.5)]">VS</span>
                      </div>
                      <div className="flex items-center gap-3 min-w-[150px] justify-start">
                        <PairAvatar
                          p1Id={match.team2.p1Id} p2Id={match.team2.p2Id}
                          p1Image={players.find(p => p.id === match.team2.p1Id)?.photoUrl}
                          p2Image={players.find(p => p.id === match.team2.p2Id)?.photoUrl}
                          className="w-8 h-8" onPlayerClick={onPlayerClick}
                        />
                        <span className={`text-sm font-black tracking-tighter transition-colors uppercase italic ${match.team2Won ? 'theme-primary-text' : 'opacity-30 group-hover:opacity-60'}`}>
                          {match.team2.name}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-center border-r border-white/5">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-30 group-hover:opacity-60 block">
                      {match.tournamentName}
                    </span>
                    {match.source === 'matchmaking' && (
                      <span className={`text-[9px] font-black uppercase tracking-widest mt-1 block ${match.matchTypeStr === 'Super 8' ? 'text-violet-400' : 'text-yellow-500'}`}>
                        {match.matchTypeStr}
                      </span>
                    )}
                  </td>
                  <td className="px-8 py-5 text-center">
                    <div className="inline-flex bg-black/60 border border-white/10 rounded-lg px-3 py-1 shadow-inner">
                      <span className="text-xs font-black theme-accent-text font-mono tracking-widest whitespace-nowrap">
                        {match.scoreStr || '---'}
                      </span>
                    </div>
                  </td>
                </LazyItem>
              ))}
            </tbody>
          </table>
        </div>

        {/* MOBILE COMPACT VIEW */}
        <div className="md:hidden">
          <div className="flex items-center justify-between px-3 py-2.5 bg-white/[0.08] text-[9px] font-black uppercase tracking-[0.2em] border-b border-white/10">
            <div className="min-w-[32px] border-r border-white/10 pr-2 text-white/50">Fecha</div>
            <div className="flex-1 text-center text-white/50">Encuentro</div>
            <div className="min-w-[36px] border-l border-white/10 pl-2 text-center text-white/50">Score</div>
          </div>

          <div className="divide-y divide-white/5">
            {filteredMatches.map((match, index) => (
              <LazyItem
                key={match.id}
                as="div"
                estimatedHeight={60}
                onClick={() => setSelectedHistoryMatch(match)}
                className={`p-2 flex items-center justify-between gap-1.5 ${index % 2 === 0 ? 'bg-white/[0.03]' : 'bg-transparent'} hover:bg-white/[0.05] transition-colors group cursor-pointer`}
              >
                <div className="flex flex-col min-w-[32px] shrink-0 border-r border-white/5 pr-2">
                  <span className="text-[9px] font-black font-mono leading-none text-white/50">
                    {match.dateStr.split('/').slice(0, 2).join('/')}
                  </span>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center gap-1 px-0.5 min-w-0">
                  <span className={`text-[7px] font-black uppercase tracking-widest truncate max-w-full ${match.source === 'matchmaking' ? (match.matchTypeStr === 'Super 8' ? 'text-violet-400' : 'text-yellow-500') : 'opacity-40'}`}>
                    {match.source === 'tournament' ? match.tournamentName : match.matchTypeStr}
                  </span>
                  <div className="flex items-center justify-center w-full gap-1">
                    <div className="flex items-center gap-1 flex-1 justify-end min-w-0">
                      <span className={`text-[8px] font-black uppercase tracking-tighter truncate ${match.team1Won ? 'theme-primary-text' : 'text-white/40'}`}>
                        {match.team1.name}
                      </span>
                      <PairAvatar p1Id={match.team1.p1Id} p2Id={match.team1.p2Id}
                        p1Image={players.find(p => p.id === match.team1.p1Id)?.photoUrl}
                        p2Image={players.find(p => p.id === match.team1.p2Id)?.photoUrl}
                        className="w-8 h-8 min-w-[32px] min-h-[32px] shrink-0 aspect-square" onPlayerClick={onPlayerClick} />
                    </div>
                    <span className="text-[6px] font-black theme-primary-text shrink-0 mx-0.5">VS</span>
                    <div className="flex items-center gap-1 flex-1 justify-start min-w-0">
                      <PairAvatar p1Id={match.team2.p1Id} p2Id={match.team2.p2Id}
                        p1Image={players.find(p => p.id === match.team2.p1Id)?.photoUrl}
                        p2Image={players.find(p => p.id === match.team2.p2Id)?.photoUrl}
                        className="w-8 h-8 min-w-[32px] min-h-[32px] shrink-0 aspect-square" onPlayerClick={onPlayerClick} />
                      <span className={`text-[8px] font-black uppercase tracking-tighter truncate ${match.team2Won ? 'theme-primary-text' : 'text-white/40'}`}>
                        {match.team2.name}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col min-w-[40px] items-center justify-center border-l border-white/5 pl-2 shrink-0">
                  <span className="text-[9px] font-black theme-accent-text font-mono tracking-tighter leading-tight text-center whitespace-nowrap bg-black/40 px-1.5 py-0.5 rounded-md border border-white/5">
                    {match.scoreStr || '-'}
                  </span>
                </div>
              </LazyItem>
            ))}
          </div>
        </div>

        {/* Empty state */}
        {!loadingTournament && filteredMatches.length === 0 && (
          <div className="py-20 text-center">
            <p className="text-sm font-black uppercase tracking-[0.2em] opacity-20">Sin partidos registrados</p>
          </div>
        )}
      </div>

      {/* Load more / loading indicator */}
      <div className="flex justify-center mt-6">
        {loadingTournament ? (
          <div className="flex items-center gap-3 text-white/40">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-xs font-black uppercase tracking-widest">Cargando...</span>
          </div>
        ) : hasMoreTournament && !searchHistory ? (
          <button
            onClick={loadMore}
            className="flex items-center gap-2 px-8 py-3 bg-white/5 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest text-white/60 hover:bg-white/10 hover:text-white transition-all"
          >
            <ChevronDown size={16} />
            Cargar más partidos
          </button>
        ) : null}
      </div>

      {selectedHistoryMatch && (
        <HistoryMatchDetailModal
          match={selectedHistoryMatch}
          players={players}
          onClose={() => setSelectedHistoryMatch(null)}
        />
      )}
    </>
  );
}
